import {
  Button, Chip, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Spinner,
} from '@heroui/react';
import { History, Search, ShieldCheck } from 'lucide-react';
import React, { FC, useState } from 'react';

import { ModalConfirmation } from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import {
  StaffClaimBackfillRun,
  StaffClaimBackfillSummary,
  useBackfillStaffClaims,
  useGetStaffClaimBackfillRuns,
} from '../../../../queries';

interface StaffClaimsDrawerProps {
  onClose: () => void;
}

const formatDate = (value: number | null): string => (value
  ? new Date(value).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const missingLabel = (missing: Array<'claim' | 'field'>): string => missing
  .map((m) => (m === 'claim' ? 'claim' : 'pole'))
  .join(' + ');

/** Tabela kont z jednego przebiegu (podgląd albo zapis). */
const SummaryTable: FC<{ summary: StaffClaimBackfillSummary | StaffClaimBackfillRun }> = ({ summary }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-wrap gap-2">
      <Chip size="sm" variant="flat">{`Przejrzano: ${summary.scanned}`}</Chip>
      <Chip size="sm" variant="flat" color="primary">
        {`${summary.dryRun ? 'Do nadania' : 'Nadano'}: ${summary.updated.length}`}
      </Chip>
      <Chip size="sm" variant="flat" color="success">{`Kompletne: ${summary.alreadyComplete}`}</Chip>
      <Chip size="sm" variant="flat" color="warning">{`Pominięte (nie-personel): ${summary.skippedNotStaff.length}`}</Chip>
      <Chip size="sm" variant="flat">{`Bez konta Auth: ${summary.missingAuth.length}`}</Chip>
      {summary.errors.length > 0 && (
        <Chip size="sm" variant="flat" color="danger">{`Błędy: ${summary.errors.length}`}</Chip>
      )}
    </div>

    {summary.updated.length > 0 && (
      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Konto</th>
              <th className="px-3 py-2">Brakowało</th>
            </tr>
          </thead>
          <tbody>
            {summary.updated.map((entry) => (
              <tr key={entry.uid} className="border-t border-gray-100">
                <td className="px-3 py-2">
                  <div className="text-gray-900">{entry.email ?? <span className="text-gray-400">brak e-mail</span>}</div>
                  <div className="font-mono text-[11px] text-gray-400">{entry.uid}</div>
                </td>
                <td className="px-3 py-2 text-gray-500">{missingLabel(entry.missing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {summary.skippedNotStaff.length > 0 && (
      <div className="rounded-xl border border-warning-200 bg-warning-50 p-3 text-xs">
        <p className="mb-1 font-semibold text-warning-700">
          Pominięte — dokument w `users` nie wygląda na personel (brak organizacji, roli, usługi ani flagi admina):
        </p>
        <ul className="flex flex-col gap-0.5 text-warning-800">
          {summary.skippedNotStaff.map((s) => (
            <li key={s.uid}>
              {s.email ?? 'brak e-mail'}
              {' '}
              <span className="font-mono text-[11px] text-warning-600">{s.uid}</span>
            </li>
          ))}
        </ul>
      </div>
    )}

    {summary.errors.length > 0 && (
      <div className="rounded-xl bg-danger-50 p-3 text-xs text-danger">
        {summary.errors.map((e) => (
          <div key={e.uid}>
            <span className="font-mono">{e.uid}</span>
            {`: ${e.message}`}
          </div>
        ))}
      </div>
    )}
  </div>
);

/**
 * Uprawnienia personelu — backfill claimu `isStaff`.
 *
 * Reguły Firestore rozpoznają personel po claimie `isStaff`; konta założone przed
 * jego wprowadzeniem go nie mają i tracą dostęp do zamówień, list śniadaniowych
 * i zgłoszeń w panelu hotelu. Przepływ: „Sprawdź" (podgląd, nic nie zapisuje) →
 * przejrzenie listy → „Nadaj" (potwierdzenie słowem). Każdy przebieg trafia do
 * historii poniżej.
 */
export const StaffClaimsDrawer: FC<StaffClaimsDrawerProps> = ({ onClose }) => {
  const { showError, showSuccess } = useHeroToast();
  const [preview, setPreview] = useState<StaffClaimBackfillSummary | null>(null);
  const [applied, setApplied] = useState<StaffClaimBackfillSummary | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openRun, setOpenRun] = useState<string | null>(null);

  const { mutate: backfill, isPending } = useBackfillStaffClaims();
  const { data: runs = [], isLoading: runsLoading } = useGetStaffClaimBackfillRuns();

  const handlePreview = () => {
    setApplied(null);
    backfill({ dryRun: true }, {
      onSuccess: (summary) => setPreview(summary),
      onError: () => showError('Nie udało się sprawdzić kont.'),
    });
  };

  const handleApply = () => {
    setShowConfirm(false);
    backfill({ dryRun: false }, {
      onSuccess: (summary) => {
        setPreview(null);
        setApplied(summary);
        showSuccess(
          `Nadano claim ${summary.updated.length} kontom. Personel dostanie go po ponownym zalogowaniu (albo do godziny).`,
        );
      },
      onError: () => showError('Nadawanie claimu nie powiodło się — sprawdź historię i błędy.'),
    });
  };

  return (
    <Drawer
      isOpen
      placement="right"
      radius="none"
      classNames={{ base: 'w-full sm:w-1/2 sm:max-w-none' }}
      onClose={onClose}
    >
      <DrawerContent>
        <DrawerHeader className="flex-col items-start gap-1">
          <span className="text-base font-semibold text-gray-900">Uprawnienia personelu (claim isStaff)</span>
          <span className="text-xs font-normal text-gray-500">
            Reguły Firestore rozpoznają personel po claimie `isStaff`. Konto bez niego nie widzi
            w panelu zamówień, list śniadaniowych ani zgłoszeń. Najpierw podgląd, potem nadanie.
          </span>
        </DrawerHeader>

        <DrawerBody className="gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="bordered"
              radius="sm"
              isLoading={isPending && !preview}
              isDisabled={isPending}
              startContent={<Search className="h-4 w-4" />}
              onPress={handlePreview}
            >
              Sprawdź (bez zapisu)
            </Button>
            <Button
              size="sm"
              color="primary"
              radius="sm"
              isDisabled={!preview || preview.updated.length === 0 || isPending}
              startContent={<ShieldCheck className="h-4 w-4" />}
              onPress={() => setShowConfirm(true)}
            >
              {preview && preview.updated.length > 0
                ? `Nadaj claim ${preview.updated.length} kontom`
                : 'Nadaj claim'}
            </Button>
          </div>

          {isPending && <Spinner size="sm" className="self-center" />}

          {preview && !isPending && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Podgląd — nic nie zostało zapisane</h3>
              {preview.updated.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                  Wszystkie konta personelu mają już claim. Nie ma nic do nadania.
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  Przejrzyj listę. Jeśli jest na niej konto, które nie powinno mieć dostępu do panelu,
                  nie nadawaj claimu — usuń je najpierw z listy użytkowników.
                </p>
              )}
              <SummaryTable summary={preview} />
            </section>
          )}

          {applied && !isPending && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-success-700">Zapisano</h3>
              <SummaryTable summary={applied} />
            </section>
          )}

          <section className="flex flex-col gap-2">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              <History className="h-4 w-4 text-gray-400" />
              Historia przebiegów
            </h3>
            {runsLoading && <Spinner size="sm" className="self-center" />}
            {!runsLoading && runs.length === 0 && (
              <p className="text-xs text-gray-500">Jeszcze nic nie uruchamiano.</p>
            )}
            {runs.map((run) => (
              <div key={run.id} className="rounded-xl border border-gray-200">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
                  onClick={() => setOpenRun(openRun === run.id ? null : run.id)}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-gray-900">
                      {formatDate(run.createdAt)}
                      {' · '}
                      <span className="text-gray-500">{run.runBy?.email ?? run.runBy?.uid}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {`${run.dryRun ? 'podgląd' : 'ZAPIS'} · ${run.dryRun ? 'do nadania' : 'nadano'} ${run.updatedCount}`}
                      {` · pominięte ${run.skippedNotStaffCount}`}
                      {run.errorCount > 0 ? ` · błędy ${run.errorCount}` : ''}
                    </div>
                  </div>
                  <Chip size="sm" variant="flat" color={run.dryRun ? 'default' : 'primary'}>
                    {run.dryRun ? 'dry-run' : 'zapis'}
                  </Chip>
                </button>
                {openRun === run.id && (
                  <div className="border-t border-gray-100 p-3">
                    <SummaryTable summary={run} />
                  </div>
                )}
              </div>
            ))}
          </section>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="light" onPress={onClose}>Zamknij</Button>
        </DrawerFooter>
      </DrawerContent>

      <ModalConfirmation
        needConfirm
        confirmValue="isStaff"
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleApply}
        title="Nadaj claim isStaff"
        description={`Nadasz claim ${preview?.updated.length ?? 0} kontom z listy podglądu. To zmiana uprawnień w Firebase Auth — wpisz „isStaff", aby potwierdzić.`}
        buttonConfirm="Nadaj"
        isLoading={isPending}
      />
    </Drawer>
  );
};
